import { createList, getLists } from 'api/list';
import { List } from 'api/list/graphqlTypes';
import fetchPhraseIfNeeded from 'utils/fetch-phrase-if-needed';
import {
    ADD_ITEMS_REQUEST,
    ADD_REQUEST,
    isFavouritesPayload,
    MERGE_SAVED_FOR_LATER,
    MultipleItemPayload,
    SAVE_FOR_LATER_REQUEST,
    UPDATE_FAVOURITES_DATA,
} from 'utils/pubsub';
import { reportError } from 'utils/sentry';
import { mergeSavedForLaterData, refreshCache } from 'utils/store';
import translate from 'utils/translate';
import { addToList } from '../../api/operations/addToList/addToList';
import { addToSavedForLaterList } from '../../api/operations/addToList/addToSavedForLaterList';

const handleAddToListSubscription = (payload: unknown) => {
    if (!isFavouritesPayload(payload)) {
        return Promise.reject('Invalid payload');
    }
    return fetchPhraseIfNeeded()
        .then(() =>
            Promise.all([import('./bootAddToList'), getLists()]).then(
                ([
                    { boot },
                    {
                        data: { lists },
                    },
                ]) => {
                    if (lists.length <= 1) {
                        return addToList({
                            payload,
                            isNewList: lists.length === 0,
                        });
                    }
                    return boot(payload, lists as List[]);
                }
            )
        )
        .catch(e => {
            reportError(e as Error);
            throw Error('Favs-agent: Could not add to favourites');
        });
};

const handleSaveForLaterSubscription = async (payload: unknown) => {
    if (!isFavouritesPayload(payload)) {
        return Promise.reject('Invalid payload');
    }
    await fetchPhraseIfNeeded();
    const {
        data: { lists },
    } = await getLists();
    const savedForLaterListId = lists.find(
        list => list.name === translate('SAVED_FOR_LATER_LIST_NAME')
    )?.listId;

    if (savedForLaterListId) {
        return addToSavedForLaterList({
            payload,
            listId: savedForLaterListId,
        });
    }
    const createdList = await createList({
        name: translate('SAVED_FOR_LATER_LIST_NAME'),
    });
    const { listId, name } = createdList.data.list;
    return addToSavedForLaterList({
        payload,
        listId,
        listName: name,
        isNewList: lists.length === 0,
    });
};

export const initAddToList = () => {
    window.ikea.pubsub.subscribe(ADD_REQUEST, handleAddToListSubscription);
    window.ikea.pubsub.subscribe(
        ADD_ITEMS_REQUEST,
        handleAddToListSubscription
    );
    window.ikea.pubsub.subscribe(
        SAVE_FOR_LATER_REQUEST,
        handleSaveForLaterSubscription
    );
    window.ikea.pubsub.subscribe(MERGE_SAVED_FOR_LATER, mergeSavedForLaterData);
    window.ikea.pubsub.subscribe(UPDATE_FAVOURITES_DATA, refreshCache);
    return (payload: MultipleItemPayload) =>
        handleAddToListSubscription(payload);
};
